import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Slider,
  SliderThumb,
  TextField,
  ThemeProvider,
  createTheme,
  styled
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { FileModel } from "../models/models";
import ReactCrop, { Crop, PixelCrop, centerCrop, makeAspectCrop } from "react-image-crop";
import { canvasPreview } from "./canvasPreview";
import colors from "../themes/colors";
import MinusIcon from "../assets/icons/MinusIcon";
import PlusIcon from "../assets/icons/PlusIcon";
import useTranslation from "../hooks/useTranslation";
import { dt } from "../themes/redesignTokens";

export interface UploadImageCropProps {
  open: boolean;
  onClose: () => void;
  onChangeFile?: (file: FileModel | null) => void;
  file: FileModel | null | undefined;
  cropVariant?: "circle" | "square" | "rectangle";
  maxFileSizeMb?: number;
  minCropWidth: number;
  modalWidth: string;
  aspectRatio: number;
}
interface ScaleSliderThumbProps extends React.HTMLAttributes<unknown> { }

const ScaleSliderThumb = (props: ScaleSliderThumbProps) => {
  const { children, ...other } = props;
  return (
    <SliderThumb {...other}>
      {children}
      <span className="scale-bar" />
    </SliderThumb>
  );
};

const ScaleSlider = styled(Slider)(({ theme }) => ({
  color: dt.blue,
  height: 3,
  padding: "13px 0",
  "& .MuiSlider-thumb": {
    height: 21,
    width: 21,
    backgroundColor: "#fff",
    border: "4px solid currentColor",
    "& .scale-bar": {
      height: 9,
      width: 9,
      borderRadius: "50%",
      backgroundColor: "currentColor",
      color: "#332B41"
    }
  },
  "& .MuiSlider-track": {
    height: 6
  },
  "& .MuiSlider-rail": {
    color: "#C0DBFB",
    opacity: 1,
    height: 8
  }
}));

const UploadImageCrop = ({
  open,
  onClose,
  onChangeFile,
  file,
  cropVariant,
  maxFileSizeMb = 5,
  minCropWidth,
  modalWidth,
  aspectRatio
}: UploadImageCropProps) => {
  const { translate } = useTranslation("common");
  const [canDrop, setCanDrop] = useState(false);
  const [error, setError] = useState<string>();

  const isCircleImage = cropVariant === "circle";
  const isRectangleImage = cropVariant === "rectangle";

  const handleFileDrop = (event: any) => {
    event.preventDefault();
    processFiles(event.dataTransfer.files as FileList);
  };

  const handleFileChange = (event: any) => {
    processFiles(event.target.files as FileList);
  };

  const processFiles = (filesList: FileList) => {
    setError(undefined);
    setScale(1);
    setCrop(undefined);

    const filesListItem = filesList[0];

    if (filesListItem.size > maxFileSizeMb * 1024 * 1024) {
      onChangeFile?.(null);
      setError(translate("uploadFile.fileIsTooBig"));
      return;
    }

    if (
      filesListItem.type !== "image/jpeg" &&
      filesListItem.type !== "image/png" &&
      filesListItem.type !== "image/jpg"
    ) {
      setError(translate("invalidFormat"));
    }

    detectUrl(filesListItem).then((url) => {
      if (url) {
        const file = {
          name: filesListItem.name,
          content: url
        };

        setNewFile(file);
      }
    });
  };

  const detectUrl = (file: Blob | null) => {
    return new Promise<string | null>((resolve, reject) => {
      if (!file) {
        resolve(null);
        return;
      }
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        resolve(reader.result as string);
      };
    });
  };

  const imgRef = useRef<HTMLImageElement>(null);
  const blobUrlRef = useRef("");
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [scale, setScale] = useState<number>(1);
  const [crop, setCrop] = useState<Crop>();
  const [newFileCrop, setNewFile] = useState<FileModel | null | undefined>(file);
  const uploadImageHeightSize = () => {
    switch (cropVariant) {
      case "circle":
        return "300px";
      case "square":
        return "400px";
      case "rectangle":
        return "117px";
    }
  };

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    const crop = makeAspectCrop(
      {
        unit: "%",
        width: 100
      },
      aspectRatio,
      width,
      height
    );

    const centeredCrop = centerCrop(crop, width, height);
    setCrop(isCircleImage ? centeredCrop : crop);
  };

  const onCloseImageCropDialog = () => {
    onClose();
    setNewFile(file || undefined);
    setCrop(undefined);
  };

  async function onSaveCropImage() {
    const image = imgRef.current;
    const previewCanvas = previewCanvasRef.current;
    if (!image || !previewCanvas || !completedCrop) {
      throw new Error("Crop canvas does not exist");
    }

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    const offscreen = new OffscreenCanvas(completedCrop.width * scaleX, completedCrop.height * scaleY);
    const ctx = offscreen.getContext("2d");
    if (!ctx) {
      throw new Error("No 2d context");
    }

    ctx.drawImage(
      previewCanvas,
      0,
      0,
      previewCanvas.width,
      previewCanvas.height,
      0,
      0,
      offscreen.width,
      offscreen.height
    );

    const blob = await offscreen.convertToBlob({
      type: "image/png"
    });

    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
    }

    blobUrlRef.current = URL.createObjectURL(blob);

    detectUrl(blob).then((url) => {
      const imageName = () => {
        switch (cropVariant) {
          case "circle":
            return "avatar";
          case "square":
            return "logo";
          case "rectangle":
            return "banner";
        }
      };
      if (url) {
        const newFile = {
          name: imageName(),
          content: url
        };

        onChangeFile?.(newFile);
        onClose();
      }
    });
  }

  useEffect(() => {
    if (completedCrop?.width && completedCrop?.height && imgRef.current && previewCanvasRef.current) {
      canvasPreview(imgRef.current, previewCanvasRef.current, completedCrop, scale);
    }
  }, [completedCrop, newFileCrop?.content, scale]);

  const theme = createTheme({
    components: {
      MuiDialog: {
        styleOverrides: {
          root: {
            ".MuiDialogContent-root": {
              padding: "0 48px 24px"
            },

            ".MuiDialogTitle-root": {
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center"
            },

            ".MuiPaper-root": {
              minWidth: modalWidth,
              display: "flex",
              justifyContent: "center"
            }
          }
        }
      }
    }
  });
  return (
    <>
      <ThemeProvider theme={theme}>
        <Dialog open={open} onClose={onClose}>
          <DialogTitle>
            <p className="heading-2">{translate("changePicture")}</p>
          </DialogTitle>

          <DialogContent>
            <Box className="h-full">
              {!newFileCrop?.content || !!error === true ? (
                <Box
                  className={`upload-file w-full h-full justify-center ${canDrop && "active"} ${!!error && "error"}`}
                  sx={{
                    justifyContent: "center",
                    borderColor: !!error ? colors.primary.red : "",
                    height: uploadImageHeightSize()
                  }}
                  onDrop={(event) => {
                    handleFileDrop(event);
                    setCanDrop(false);
                  }}
                  onDragOver={(event) => {
                    event.preventDefault();
                    setCanDrop(true);
                  }}
                  onDragLeave={() => setCanDrop(false)}
                >
                  <Box className="flex flex-col items-center justify-center gap-2">
                    <p className="text-uppercase text-center text-12-regular">{translate("uploadFile.dragAndDrop")}</p>
                    <p className="text-uppercase text-center text-12-regular">{translate("uploadFile.or")}</p>
                    <label htmlFor="image-input">
                      <Button component="span" variant="outlined">
                        {translate("chooseImage")}
                      </Button>
                    </label>
                    {!!error && (
                      <FormHelperText
                        error
                        sx={{
                          padding: "6px 8px",
                          margin: 0,
                          textAlign: "center",
                          minHeight: "40px",
                          maxWidth: "370px"
                        }}
                      >
                        {error}
                      </FormHelperText>
                    )}
                  </Box>
                </Box>
              ) : (
                <>
                  <Box className="flex flex-col items-center">
                    <ReactCrop
                      crop={crop}
                      onChange={(_, procentCrop) => setCrop(procentCrop)}
                      onComplete={(c) => setCompletedCrop(c)}
                      aspect={aspectRatio}
                      circularCrop={isCircleImage}
                      keepSelection
                      minWidth={minCropWidth}
                      minHeight={isRectangleImage ? 100 : undefined}
                    >
                      <img
                        src={newFileCrop?.content}
                        ref={imgRef}
                        crossOrigin="anonymous"
                        onLoad={onImageLoad}
                        style={{ transform: `scale(${scale})`, backgroundSize: "cover" }}
                      />
                    </ReactCrop>

                    <Box className="flex gap-4 justify-center items-center mt-2 mb-2">
                      <MinusIcon />
                      <ScaleSlider
                        sx={{
                          width: "430px"
                        }}
                        slots={{ thumb: ScaleSliderThumb }}
                        value={scale}
                        onChange={(e: any) => setScale(Number(e.target.value))}
                        step={0.1}
                        min={1}
                        max={3}
                      />
                      <PlusIcon />
                    </Box>
                  </Box>
                </>
              )}

              <canvas
                ref={previewCanvasRef}
                hidden
                style={{
                  objectFit: "cover",
                  width: completedCrop?.width,
                  height: completedCrop?.height
                }}
              />

              <TextField
                type="file"
                id="image-input"
                style={{ display: "none" }}
                onChange={handleFileChange}
                onClick={(event: any) => {
                  event.target.value = null;
                }}
                inputProps={{ accept: ".jpeg, .jpg, .png" }}
              />
            </Box>
          </DialogContent>

          <DialogActions
            className="flex"
            sx={{ "&.MuiDialogActions-root": { padding: "0 48px 24px !important", width: "100%" } }}
          >
            {(!file?.content && !newFileCrop?.content) || !!error === true ? (
              <Button type="submit" variant="contained" onClick={onClose}>
                {translate("buttons.close")}
              </Button>
            ) : (
              <Box className="flex justify-between w-full mt-2">
                <label htmlFor="image-input">
                  <Button component="span" variant="outlined">
                    {translate("changeImage")}
                  </Button>
                </label>
                <Box className="flex gap-4">
                  <Button variant="outlined" onClick={onCloseImageCropDialog}>
                    {translate("buttons.cancel")}
                  </Button>
                  <Button variant="contained" onClick={onSaveCropImage}>
                    {translate("buttons.save")}
                  </Button>
                </Box>
              </Box>
            )}
          </DialogActions>
        </Dialog>
      </ThemeProvider>
    </>
  );
};
export default UploadImageCrop;